if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.rowWindow !== 'true') return;
        this.rowConnectFrame = requestAnimationFrame(() => this.initRowWindow());
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.rowConnectFrame);
        cancelAnimationFrame(this.rowMeasureFrame);
        this.cancelRowAnimation();
        this.rowList?.querySelectorAll('[data-media-id]').forEach(item => this.restoreRowItem(item));
        this.rowResizeObserver?.disconnect();
        this.rowMutationObserver?.disconnect();
        if (this.rowModeChange) this.rowSmallMode?.removeEventListener('change', this.rowModeChange);
        this.restoreMobileHeading();
        if (this.rowPointerDown) this.rowViewport?.removeEventListener('pointerdown', this.rowPointerDown);
        if (this.rowPointerUp) window.removeEventListener('pointerup', this.rowPointerUp);
        if (this.rowPointerCancel) window.removeEventListener('pointercancel', this.rowPointerCancel);
        if (this.rowSuppressClick) this.rowViewport?.removeEventListener('click', this.rowSuppressClick, true);
        if (this.rowImageLoad) this.elements.viewer?.removeEventListener('load', this.rowImageLoad, true);
        if (this.rowControls) {
          this.rowControls.removeEventListener('click', this.rowControlClick);
          this.rowControls.removeEventListener('keydown', this.rowControlKey);
        }
      }

      initRowWindow() {
        this.rowViewport = this.querySelector('[data-row-viewport]');
        this.rowControls = this.querySelector('[data-row-controls]');
        this.rowList = this.rowViewport?.querySelector('.product__media-list');
        if (!this.rowViewport || !this.rowList || !this.rowControls) return;
        this.rowSmallMode = window.matchMedia('(max-width: 770px)');
        this.setupMobileHeading();
        this.setupRowSwipe();
        this.rowStart = 0;
        this.rowSavedAttributes = new WeakMap();
        this.rowPrevious = this.rowControls.querySelector('[data-row-previous]');
        this.rowNext = this.rowControls.querySelector('[data-row-next]');
        this.rowModeChange = () => { this.rowStart = 0; this.cancelRowAnimation(); this.rowViewport.scrollTop = 0; this.rowViewport.scrollLeft = 0; this.updateMobileHeading(); this.measureRowWindow(); };
        this.rowControlClick = (event) => {
          const button = event.target.closest('[data-row-previous], [data-row-next]');
          if (!button || button.disabled) return;
          this.moveRowWindow(button.hasAttribute('data-row-next') ? 1 : -1);
        };
        this.rowControlKey = (event) => {
          const previous = this.isHorizontalRowWindow() ? 'ArrowLeft' : 'ArrowUp';
          const next = this.isHorizontalRowWindow() ? 'ArrowRight' : 'ArrowDown';
          if (event.key !== previous && event.key !== next) return;
          event.preventDefault();
          this.moveRowWindow(event.key === next ? 1 : -1);
        };
        this.rowImageLoad = () => this.queueRowMeasure();
        this.rowControls.addEventListener('click', this.rowControlClick);
        this.rowControls.addEventListener('keydown', this.rowControlKey);
        this.elements.viewer.addEventListener('load', this.rowImageLoad, true);
        this.rowSmallMode.addEventListener('change', this.rowModeChange);
        this.rowResizeObserver = new ResizeObserver(() => this.queueRowMeasure());
        this.rowResizeObserver.observe(this.rowList);
        this.rowMutationObserver = new MutationObserver(() => {
          this.cancelRowAnimation();
          this.queueRowMeasure();
        });
        this.rowMutationObserver.observe(this.rowList, { childList: true });
        this.measureRowWindow();
      }

      isRowWindowActive() {
        return this.dataset.rowWindow === 'true' && !!this.rowViewport;
      }

      isSmallRowWindow() { return !!this.rowSmallMode?.matches; }
      isHorizontalRowWindow() { return this.dataset.courseHorizontal === 'true' || this.isSmallRowWindow(); }
      rowWindowCount() { return this.isHorizontalRowWindow() ? 1 : 3; }
      rowScrollProperty() { return this.isHorizontalRowWindow() ? 'scrollLeft' : 'scrollTop'; }

      setupMobileHeading() {
        this.mobileProduct = this.closest('product-info');
        this.mobilePanel = this.mobileProduct?.querySelector('.pdp-reference-panel');
        this.mobileGrid = this.closest('.product');
        if (!this.mobilePanel || !this.mobileGrid) return;
        this.mobileHeading = document.createElement('div');
        this.mobileHeading.className = 'pdp-mobile-heading';
        this.mobileHeading.hidden = true;
        this.mobileHeadingItems = ['title','vendor','reviews','price'].map(name => {
          const node = Array.from(this.mobilePanel.children).find(child => child.matches('.pdp-' + name));
          if (!node) return null;
          const marker = document.createComment('mobile heading: ' + name);
          node.parentNode.insertBefore(marker,node);
          return {node,marker};
        }).filter(Boolean);
        this.mobileGrid.insertBefore(this.mobileHeading,this.mobileGrid.firstChild);
        this.updateMobileHeading();
      }

      updateMobileHeading() {
        if (!this.mobileHeading) return;
        if (this.isSmallRowWindow()) {
          this.mobileHeadingItems.forEach(({node}) => this.mobileHeading.appendChild(node));
          this.mobileHeading.hidden = false;
        } else {
          this.mobileHeadingItems.forEach(({node,marker}) => marker.parentNode?.insertBefore(node,marker.nextSibling));
          this.mobileHeading.hidden = true;
        }
      }

      restoreMobileHeading() {
        this.mobileHeadingItems?.forEach(({node,marker}) => {
          marker.parentNode?.insertBefore(node,marker.nextSibling);
          marker.remove();
        });
        this.mobileHeading?.remove();
        this.mobileHeading = null;
      }

      setupRowSwipe() {
        this.rowPointerDown = event => {
          if (!this.isHorizontalRowWindow() || !event.isPrimary || event.button !== 0) return;
          if (event.target.closest?.('video, iframe, deferred-media[loaded], product-model[loaded], input, textarea')) return;
          this.rowTouchStart = {x:event.clientX,y:event.clientY,id:event.pointerId};
        };
        this.rowPointerUp = event => {
          const start = this.rowTouchStart;
          this.rowTouchStart = null;
          if (!start || start.id !== event.pointerId || !this.isHorizontalRowWindow()) return;
          const dx = event.clientX-start.x;
          const dy = event.clientY-start.y;
          if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)*1.2) return;
          this.rowIgnoreClickUntil = performance.now()+500;
          this.moveRowWindow(dx < 0 ? 1 : -1);
        };
        this.rowPointerCancel = () => {this.rowTouchStart = null;};
        this.rowSuppressClick = event => {
          if (performance.now() >= (this.rowIgnoreClickUntil || 0)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        this.rowViewport.addEventListener('pointerdown',this.rowPointerDown);
        window.addEventListener('pointerup',this.rowPointerUp);
        window.addEventListener('pointercancel',this.rowPointerCancel);
        this.rowViewport.addEventListener('click',this.rowSuppressClick,true);
      }

      queueRowMeasure() {
        if (this.rowAnimating) { this.rowNeedsMeasure = true; return; }
        cancelAnimationFrame(this.rowMeasureFrame);
        this.rowMeasureFrame = requestAnimationFrame(() => this.measureRowWindow());
      }

      getRowWindowRows() {
        const listRect = this.rowList.getBoundingClientRect();
        const listTop = listRect.top;
        const rows = [];
        const items = Array.from(this.rowList.children).filter(item => item.hasAttribute('data-media-id') && item.getBoundingClientRect().width > 0);
        if (this.isHorizontalRowWindow()) return items.map(item => {
          const rect = item.getBoundingClientRect();
          return {top:rect.left-listRect.left, height:rect.height, items:[item]};
        });
        items.forEach(item => {
          const rect = item.getBoundingClientRect();
          const top = rect.top - listTop;
          let row = rows[rows.length - 1];
          if (!row || Math.abs(row.top - top) > 2) {
            row = { top, bottom: top + rect.height, items: [] };
            rows.push(row);
          }
          row.items.push(item);
          row.bottom = Math.max(row.bottom, top + rect.height);
        });
        return rows;
      }

      restoreRowItem(item) {
        const saved = this.rowSavedAttributes?.get(item);
        if (!saved) return;
        if (saved.aria === null) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', saved.aria);
        item.inert = saved.inert;
      }

      measureRowWindow() {
        if (!this.rowViewport || !this.isConnected) return;
        if (!this.isRowWindowActive()) {
          this.classList.remove('gallery-row-window-ready');
          this.rowViewport.style.removeProperty('height');
          this.rowViewport.scrollTop = 0;
          this.rowControls.hidden = true;
          this.rowList.querySelectorAll('[data-media-id]').forEach(item => this.restoreRowItem(item));
          return;
        }
        this.classList.add('gallery-row-window-ready');
        this.rowViewport[this.isHorizontalRowWindow() ? 'scrollTop' : 'scrollLeft'] = 0;
        this.rowPrevious.setAttribute('aria-label', this.isHorizontalRowWindow() ? 'Show previous image' : 'Show previous row of images');
        this.rowNext.setAttribute('aria-label', this.isHorizontalRowWindow() ? 'Show next image' : 'Show next row of images');
        this.rowRows = this.getRowWindowRows();
        if (!this.rowRows.length) return;
        const maximum = Math.max(0, this.rowRows.length - this.rowWindowCount());
        this.rowStart = Math.max(0, Math.min(this.rowStart || 0, maximum));
        const position = this.rowWindowPosition(this.rowStart);
        this.rowViewport.style.height = `${position.height}px`;
        this.rowViewport[this.rowScrollProperty()] = position.top;
        this.updateRowWindowAccess(false);
      }

      rowWindowPosition(start) {
        const rows = this.rowRows;
        const first = rows[start];
        if (this.isHorizontalRowWindow()) return {top:first.top, height:Math.max(1, this.dataset.courseHorizontal === 'true' ? rows[0].height : first.height)};
        const last = rows[Math.min(start + this.rowWindowCount() - 1, rows.length - 1)];
        return { top: first.top, height: Math.max(1, last.bottom - first.top) };
      }

      cancelRowAnimation() {
        cancelAnimationFrame(this.rowAnimationFrame);
        this.rowAnimationFrame = null;
        this.rowAnimating = false;
      }

      moveRowWindow(direction) {
        if (!this.isRowWindowActive() || this.rowAnimating || !this.rowRows?.length) return;
        const targetStart = Math.max(0, Math.min(this.rowStart + direction, Math.max(0, this.rowRows.length - this.rowWindowCount())));
        if (targetStart === this.rowStart) return;
        const scrollProperty = this.rowScrollProperty();
        const fromTop = this.rowViewport[scrollProperty];
        const fromHeight = this.rowViewport.getBoundingClientRect().height;
        const target = this.rowWindowPosition(targetStart);
        this.rowStart = targetStart;
        this.rowAnimating = true;
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const duration = reduced ? 0 : 450;
        let began;
        const finish = () => {
          this.rowViewport.style.height = `${target.height}px`;
          this.rowViewport[scrollProperty] = target.top;
          this.rowAnimating = false;
          this.rowAnimationFrame = null;
          this.updateRowWindowAccess(true);
          if (this.rowNeedsMeasure) { this.rowNeedsMeasure = false; this.queueRowMeasure(); }
        };
        const frame = (time) => {
          if (!this.isConnected || !this.isRowWindowActive()) { this.cancelRowAnimation(); return; }
          if (began === undefined) began = time;
          const progress = Math.min(1, (time - began) / duration);
          const eased = progress * progress * (3 - 2 * progress);
          this.rowViewport.style.height = `${fromHeight + (target.height - fromHeight) * eased}px`;
          this.rowViewport[scrollProperty] = fromTop + (target.top - fromTop) * eased;
          if (progress < 1) this.rowAnimationFrame = requestAnimationFrame(frame);
          else finish();
        };
        if (duration === 0) finish();
        else this.rowAnimationFrame = requestAnimationFrame(frame);
      }

      updateRowWindowAccess(announce) {
        const shownRows = this.rowRows.slice(this.rowStart, this.rowStart + this.rowWindowCount());
        const visible = new Set(shownRows.flatMap(row => row.items));
        const allItems = this.rowRows.flatMap(row => row.items);
        if (this.isHorizontalRowWindow()) allItems.forEach(item => item.classList.toggle('is-active',visible.has(item)));
        allItems.forEach(item => {
          if (!this.rowSavedAttributes.has(item)) this.rowSavedAttributes.set(item, { aria: item.getAttribute('aria-hidden'), inert: item.inert });
          if (visible.has(item)) this.restoreRowItem(item);
          else { item.setAttribute('aria-hidden', 'true'); item.inert = true; }
        });
        this.rowControls.hidden = this.rowRows.length <= this.rowWindowCount();
        this.rowPrevious.disabled = this.rowStart === 0;
        this.rowNext.disabled = this.rowStart >= Math.max(0, this.rowRows.length - this.rowWindowCount());
        if (announce && this.elements.liveRegion) {
          const first = allItems.indexOf(shownRows[0].items[0]) + 1;
          const last = allItems.indexOf(shownRows[shownRows.length - 1].items.slice(-1)[0]) + 1;
          this.elements.liveRegion.removeAttribute('aria-hidden');
          this.elements.liveRegion.textContent = `Showing images ${first} through ${last} of ${allItems.length}`;
        }
      }

      revealRowMedia(item) {
        if (!this.isRowWindowActive()) return;
        this.cancelRowAnimation();
        this.rowRows = this.getRowWindowRows();
        const rowIndex = this.rowRows.findIndex(row => row.items.includes(item));
        if (rowIndex < 0) return;
        if (rowIndex < this.rowStart || rowIndex >= this.rowStart + this.rowWindowCount()) this.rowStart = Math.min(rowIndex, Math.max(0, this.rowRows.length - this.rowWindowCount()));
        this.measureRowWindow();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (this.isRowWindowActive()) this.revealRowMedia(activeMedia);
        if (!this.isRowWindowActive()) window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}



